<template>
  <app-action-bar
    :menu-items="actionBarItems"
    :selected-items="selectedDocuments"
    data-locator="multi-select-bar"
    @clear-selection="onClearSelection"
  >
    <app-action-bar-list-item
      v-for="item in actionBarItems"
      :item="item"
      :key="item.name"
    >
      <PolyOfficeMultiSelectBarButtonExport
        v-if="item.name === 'poly'"
        :fileSizes="fileSelectionSizes"
        @polyOfficeOpenDialog="onExportToPoly"
      />
    </app-action-bar-list-item>

    <IManageFolderPickerDialog
      v-if="imanageEnabledAndLogged"
      v-model:is-open="isExportToImanageDialogOpen"
      @folders="prepareExportFildersToImanage"
    />
  </app-action-bar>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

import IManageFolderPickerDialog from '@/common/imanage/IManageFolderPickerDialog.vue'
import { isPolyOfficeExportDisabled } from '@/common/polyOffice/exportability'
import { DocumentsExport } from '@/common/polyOffice/polyOffice.model'
import PolyOfficeMultiSelectBarButtonExport from '@/common/polyOffice/PolyOfficeMultiSelectBarButtonExport.vue'
import { canFilesBeAddedToChecklist } from '@/common/utils/signingChecklist'
import { toast } from '@/common/utils/toast'
import { ARCHIVES_ADD_ROUTE_NAME, SIGNING_CHECKLIST_ADD_ROUTE_NAME } from '@/router/routerData'
import {
  DESELECT_ALL_DOCUMENTS,
  GET_PAGINATED_DOCUMENTS,
  SET_TABLE_DOCUMENTS_PANE,
} from '@/store/modules/documents/action_types'
import { DocumentsDeleteActionTypes } from '@/store/modules/documents-delete/action_types'
import { OPEN_DOCUMENTS_DOWNLOAD_FILES } from '@/store/modules/documents-download-files/action_types'
import { POST_FILDERS_TO_IMANAGE } from '@/store/modules/imanage/action_types'
import { GET_EXPORT_IFRAME } from '@/store/modules/poly-office/action_types'

export default {
  name: 'DocumentsActionBar',
  components: {
    PolyOfficeMultiSelectBarButtonExport,
    IManageFolderPickerDialog,
  },
  data () {
    return {
      ARCHIVES_ADD_ROUTE_NAME,
      SIGNING_CHECKLIST_ADD_ROUTE_NAME,
      isExportToImanageDialogOpen: false,
    }
  },
  computed: {
    ...mapState('documents', ['selectedDocuments']),
    ...mapState('room', ['currentRoom']),
    ...mapGetters('imanage', ['imanageEnabledAndLogged']),
    ...mapGetters('room', ['isCurrentUserPm', 'isDataRoom', 'currentUserRights', 'maxSignableSize']),
    ...mapGetters('user', ['currentUserId', 'polyOfficeEnabled', 'isArchivingEnabled']),
    ...mapGetters('groups', ['currentUserGroupId']),
    archiveAddRouteOptions () {
      return {
        state: {
          files: this.selectedDocuments,
        },
        query: {
          fileIds: this.selectedDocuments.map((doc) => doc.id).toString(),
          fromMnemo: this.currentRoom.mnemo,
        },
      }
    },
    actionBarItems () {
      return [
        {
          action: () => { this.onShare() },
          icon: 'share',
          hidden: !this.selectionCanBeShared,
          name: 'share',
          title: this.$t('common.share'),
          locatorValue: 'share-bar-item',
        },
        {
          action: () => { this.onMove() },
          icon: 'subdirectory_arrow_right',
          hidden: !this.canSelectionBeWritten,
          name: 'move',
          title: this.$t('project.documents.DocumentsActionBar.move'),
          locatorValue: 'move-bar-item',
        },
        {
          action: () => { this.onDownload() },
          icon: 'download',
          hidden: !this.selectionCanBeDownloaded,
          locatorValue: 'download-bar-item',
          name: 'download',
          title: this.$t('common.download'),
        },
        {
          action: () => { this.onDelete() },
          icon: 'delete',
          hidden: !this.canSelectionBeWritten,
          locatorValue: 'delete-bar-item',
          name: 'delete',
          style: 'text-error',
          title: this.$t('common.delete'),
          variant: 'danger',
        },
        {
          action: () => { this.onAddToArchives() },
          icon: 'inventory_2',
          hidden: !this.canFilesBeArchived,
          locatorValue: 'archive-bar-item',
          name: 'archive',
          title: this.$t('common.archives.ArchiveAddAccess.text'),
        },
        {
          action: () => { this.onAddToSigningChecklist() },
          icon: 'signature',
          hidden: !this.canFilesBeAddedToChecklist,
          locatorValue: 'add-to-signing-checklist-bar-item',
          name: 'add',
          title: this.$t('project.documents.table.DocumentsTableItemMenu.signingChecklistAdd'),
        },
        {
          action: () => { this.onExportToPoly() },
          iconComponent: 'app-icon-poly-office',
          disabled: this.isExportToPolyDisabled,
          hidden: !this.canFilesBeExportedToPoly,
          locatorValue: 'poly-export-bar-item',
          name: 'poly',
          title: this.$t('common.polyOffice.PolyOfficeMultiSelectBarButtonExport.exportFiles'),
        },
        {
          action: () => { this.onExportToImanage() },
          iconComponent: 'app-icon-imanage',
          hidden: !this.canFilesBeExportedToImanage,
          locatorValue: 'imanage-export-bar-item',
          name: 'imanage',
          title: this.$t('project.documents.DocumentsActionBar.exportToImanage'),
        },
      ]
    },
    canFilesBeArchived () {
      return this.isDocumentAllFiles && this.isArchivingEnabled
    },
    canFilesBeAddedToChecklist () {
      return !this.isDataRoom && canFilesBeAddedToChecklist(this.selectedDocuments, this.isCurrentUserPm, this.maxSignableSize) && this.isDocumentAllFiles
    },
    canFilesBeExportedToImanage () {
      return this.imanageEnabledAndLogged && this.isDocumentAllFiles
    },
    canFilesBeExportedToPoly () {
      return this.polyOfficeEnabled && this.isDocumentAllFiles
    },
    canSelectionBeWritten () {
      if (this.isSelectionQaFolderOrChild) {
        return false
      }
      return this.selectedDocuments.every(doc => {
        if (doc.type !== 'file' && this.isSelectionTodoFolderOrChild) return false
        if (doc.canWrite) return true
        if (doc.rights?.write) {
          return doc.rights.write.groups.find((group) => group.id === this.currentUserGroupId) ||
            doc.rights.write.users.find((user) => user.id === this.currentUserId)
        }
        return false
      })
    },
    fileSelectionSizes () {
      return this.selectedDocuments
        .filter((item) => item.type === 'file')
        .map((item) => item.size)
    },
    isDocumentAllFiles () {
      return this.selectedDocuments.every((doc) => doc.type === 'file')
    },
    isExportToPolyDisabled () {
      return isPolyOfficeExportDisabled(this.fileSelectionSizes)
    },
    isSelectionTodoFolderOrChild () {
      return this.$store.getters['documentsBreadcrumb/hasTodoFolderOrChild']
    },
    isSelectionQaFolderOrChild () {
      return this.$store.getters['documentsBreadcrumb/hasQaFolderOrChild']
    },
    selectionCanBeDownloaded () {
      return this.currentUserRights.canDownload
    },
    selectionCanBeShared () {
      return this.isCurrentUserPm || this.selectedDocuments.every(doc => doc.canShare)
    },
  },
  methods: {
    ...mapActions('documents', [DESELECT_ALL_DOCUMENTS, SET_TABLE_DOCUMENTS_PANE, GET_PAGINATED_DOCUMENTS]),
    ...mapActions('documentsDelete', [DocumentsDeleteActionTypes.OPEN_DOCUMENTS_DELETION_DIALOG]),
    ...mapActions('imanage', [POST_FILDERS_TO_IMANAGE]),
    ...mapActions('polyOffice', [GET_EXPORT_IFRAME]),
    ...mapActions('documentsDownloadFiles', [OPEN_DOCUMENTS_DOWNLOAD_FILES]),
    onAddToArchives () {
      this.$router.push(
        {
          name: ARCHIVES_ADD_ROUTE_NAME,
          ...this.archiveAddRouteOptions,
        },
      )
    },
    onAddToSigningChecklist () {
      this.$router.push(
        {
          name: SIGNING_CHECKLIST_ADD_ROUTE_NAME,
          state: {
            files: this.selectedDocuments,
          },
          query: {
            fileIds: this.selectedDocuments.map((document) => document.id).toString(),
          },
        },
      )
    },
    onClearSelection () {
      this.DESELECT_ALL_DOCUMENTS()
    },
    onDelete () {
      this.OPEN_DOCUMENTS_DELETION_DIALOG(this.selectedDocuments)
    },
    onDownload () {
      this.OPEN_DOCUMENTS_DOWNLOAD_FILES(this.selectedDocuments)
    },
    onExportToImanage () {
      this.isExportToImanageDialogOpen = true
    },
    async onExportToPoly () {
      const payload = this.preparePolyOfficePayload()
      try {
        await this.GET_EXPORT_IFRAME({
          payload,
          successMessage: this.$t('project.documents.DocumentsActionBar.polyOffice.exportSuccessful'),
        })
      } catch (error) {
        console.error(error)
        toast.error(this.$t('common.polyOfficeErrors.ERR_FILE_TOO_LARGE', 1))
      }
    },
    onMove () {
      this.SET_TABLE_DOCUMENTS_PANE({ isOpen: true, documents: this.selectedDocuments, tab: 'move' })
    },
    onShare () {
      this.SET_TABLE_DOCUMENTS_PANE({ isOpen: true, documents: this.selectedDocuments, tab: 'share', forcePmSelection: true })
    },
    async prepareExportFildersToImanage (target) {
      const data = {
        files: [],
        folders: [],
        targetId: target.selected,
      }
      for (const doc of this.selectedDocuments) {
        if (doc.type === 'folder') {
          data.folders.push(doc.id)
        } else if (doc.type === 'file') {
          data.files.push(doc.id)
        }
      }
      try {
        await this.POST_FILDERS_TO_IMANAGE({
          mnemo: this.$route.params.mnemo,
          data,
        })
        toast.success(this.$t('project.documents.DocumentsActionBar.exportFildersToImanageSuccess'))
        this.GET_PAGINATED_DOCUMENTS()
        this.DESELECT_ALL_DOCUMENTS()
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.documents.DocumentsActionBar.exportFildersToImanageError'))
      }
    },
    preparePolyOfficePayload () {
      const selectedFileIds = this.selectedDocuments.reduce((acc, item) => {
        return [
          ...acc,
          item.id,
        ]
      }, [])

      return new DocumentsExport(this.currentRoom.mnemo, selectedFileIds)
    },
  },
}
</script>
